import { Spinner, useOutsideClick } from '@chakra-ui/react';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import classNames from 'classnames';
import {
  forwardRef,
  type Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import type { TNode, TreeSelectionComponentProps } from '@revelio/ui';
import {
  Input,
  searchMatch,
  ThemedSearchIcon,
  type TreeApi,
  type TreeApiBaseProps,
  type TreeApiControlProps,
  useTreeApi,
} from '@revelio/ui';

import { useToast } from '../../../../hooks/useToast';
import { isEventInsideEntityInfoPopover } from '../../EntityInfoPopover';
import { BranchLabel } from '../BranchLabel';
import styles from '../PopoutTree.module.css';
import { useDrillMode } from '../useDrillMode';
import layoutStyles from './async-company-search-tree.module.css';
import { SplitResultsTree } from './SplitResultsTree';
import { type Branch, UnifiedPopoutTree } from './UnifiedPopoutTree';
import { useCompanySearch } from './useCompanySearchV2';

const TREE_HEIGHT = 230;
const TOP_SECTION_HEIGHT = 24;

const HEIGHT = TREE_HEIGHT - TOP_SECTION_HEIGHT;

export type AsyncCompanySearchTreeProps = Omit<
  TreeSelectionComponentProps,
  'treeApi' | 'drillMode' | 'onClick'
> &
  Omit<TreeApiBaseProps, 'openByDefault'> &
  TreeApiControlProps & {
    branches?: Branch[] | ((node: TNode) => Branch);
    disabledLevels?: number[];
    searchInputRef?: React.RefObject<HTMLInputElement>;
    selectedNodeData?: TNode[];
    isOpen?: boolean;
    middleContent?: React.ReactNode;
    recentContent?: React.ReactNode;
    /**
     * Select companies only: a flat headcount-ordered list instead of the
     * industry drill tree, and no Industry tab in the search results.
     */
    companyOnly?: boolean;
  };

export const AsyncCompanySearchTree = forwardRef(
  (
    {
      branches,
      disabledLevels,
      className,
      sortSuggestedSearch,
      searchInputRef,
      selectedNodeData = [],
      middleContent,
      recentContent,
      companyOnly = false,
      ...props
    }: AsyncCompanySearchTreeProps,
    ref: Ref<TreeApi | undefined>
  ) => {
    const companyIndustryTreeRef = useRef<{
      scrollToTop: () => void;
    }>();

    const { data } = props;

    const unifiedTreeApi = useTreeApi({
      ...props,
      data,
      openByDefault: false,
    });

    const { search, onSearch } = unifiedTreeApi;

    const {
      data: searchResults,
      rawMappedResults,
      initialIndustries,
      isLoading,
      error,
      inputValue,
      setInputValue,
      handleChange,
    } = useCompanySearch(search, onSearch, data, selectedNodeData);

    useImperativeHandle(ref, () => {
      return {
        ...unifiedTreeApi,
        onResetSearch,
        scrollToTop: () => companyIndustryTreeRef.current?.scrollToTop(),
      };
    });

    const [branchLabel, setBranchLabel] = useState<Branch | null>(null);
    const [activeTab, setActiveTab] = useState(0);

    const toast = useToast();

    useEffect(() => {
      if (error) {
        toast({
          title: 'Search error',
          description:
            'Something went wrong searching all companies. Showing results from a limited set of companies. Please try again later.',
          status: 'error',
          duration: 6000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }, [error, toast]);

    const popoutTreeRef = useRef<HTMLDivElement>(null);

    const isSearchEmpty = search.length === 0;

    const isDrillEnabled = isSearchEmpty;

    const {
      drillState,
      drillIntoNode,
      drillOutToLevel,
      isDrilledInto,
      clearDrillState,
    } = useDrillMode();

    const onResetSearch = () => {
      setInputValue('');
      onSearch('');
    };

    useOutsideClick({
      ref: popoutTreeRef,
      handler: (event) => {
        if (!isSearchEmpty) return;
        // A click inside the portaled entity-info tooltip isn't a click away.
        if (isEventInsideEntityInfoPopover(event)) return;

        clearDrillState();
      },
    });

    useEffect(() => {
      if (
        typeof branches === 'function' &&
        unifiedTreeApi.treeRef?.current?.root
      ) {
        setBranchLabel(branches(unifiedTreeApi.treeRef.current.root));
      } else if (Array.isArray(branches) && branches[0]) {
        setBranchLabel(branches[0]);
      }
    }, [branches, unifiedTreeApi.treeRef]);

    const isSearchMatch = (node: TNode, search: string) => {
      return searchMatch(node, search);
    };

    const searchGlyph = <ThemedSearchIcon className={styles.searchIcon} />;

    return (
      <div
        className={classNames(styles.popoutTree, className)}
        ref={popoutTreeRef}
      >
        <div
          className={styles.searchGroup}
          onClick={() => {
            clearDrillState();
          }}
        >
          <Input
            size="sm"
            value={inputValue}
            onChange={handleChange}
            placeholder="Search"
            data-testid="tree-search-input"
            autoFocus
            ref={searchInputRef}
            className={styles.searchInput}
            wrapperClassName={styles.searchField}
            endSlot={
              <>
                {isLoading && (
                  <div className={layoutStyles.searchLoading}>
                    <Spinner
                      w={3.5}
                      h={3.5}
                      color="green.600"
                      className={styles.searchSpinner}
                      data-testid="async-tree-search-input-loading"
                    />
                  </div>
                )}
                {isSearchEmpty ? (
                  searchGlyph
                ) : (
                  <IconCrossSmall
                    data-testid="async-tree-search-input-clear-icon"
                    size={16}
                    className={classNames(styles.searchIcon, styles.clearIcon)}
                    onClick={onResetSearch}
                  />
                )}
              </>
            }
          />
        </div>

        {isSearchEmpty && branchLabel && !companyOnly && (
          <div
            className={layoutStyles.branchLabelRow}
            onClick={() => {
              clearDrillState();
            }}
          >
            <BranchLabel
              branch={branchLabel.label}
              branchDescription={branchLabel.description}
            />
          </div>
        )}
        {middleContent && <div>{middleContent}</div>}
        {recentContent && inputValue.length === 0 && <div>{recentContent}</div>}
        <div className={styles.rootTreeContainer} data-tree-root>
          {isSearchEmpty && !companyOnly ? (
            <UnifiedPopoutTree
              {...props}
              data={searchResults}
              treeApi={unifiedTreeApi}
              disabledLevels={disabledLevels}
              height={HEIGHT}
              drillMode={{
                isEnabled: isDrillEnabled,
                level: 0,
                isRoot: true,
                drillIntoNode,
                drillOutToLevel,
                ...(isDrillEnabled && { isDrilledInto }),
              }}
              sortSuggestedSearch={sortSuggestedSearch && !isSearchEmpty}
              drillState={drillState}
              branches={branches}
              isSearchMatch={isSearchMatch}
              isLoading={isLoading}
              onDrillStateChange={(level) => {
                drillOutToLevel(level);
              }}
              ref={companyIndustryTreeRef}
            />
          ) : (
            <SplitResultsTree
              {...props}
              companyResults={rawMappedResults}
              industryResults={initialIndustries}
              onCompanyChange={(ids, nodes) => {
                props.onChange?.(ids, nodes);
              }}
              onIndustryChange={(ids) => {
                props.onChange?.(ids);
              }}
              height={HEIGHT}
              isLoading={isLoading}
              search={search}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              companyOnly={
                companyOnly ||
                (disabledLevels?.includes(0) && disabledLevels?.includes(1))
              }
            />
          )}
        </div>
      </div>
    );
  }
);
